import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Shield, Heart, Sparkle } from '@phosphor-icons/react';
import WhyMembersStay from '../components/billing/WhyMembersStay';

/**
 * Stage 2-P0 — Pricing page.
 *
 * Editorial calm layout matching Kindra design system. Reads like a private
 * letter from someone who actually paid attention, not like a SaaS pricing page.
 *
 *   /pricing
 *
 * Routes:
 *   • CTA "Start Free Trial" → /register
 *   • CTA "See How It Works" → /
 *   • "Sign in" → /login
 */

const FAQ = [
  {
    q: 'Do I need a credit card to start?',
    a: 'No. Your 30-day free trial begins the moment you create your account. We will only ask for a payment method if you choose to continue past day 30.',
  },
  {
    q: 'What happens after 30 days?',
    a: 'On day 31, you can choose to continue with Kindra Active Membership ($9.99 AUD/month), or step away. Your record stays — we will never delete your data because a trial ended.',
  },
  {
    q: 'Can I cancel any time?',
    a: 'Yes. One click in your billing settings. No phone calls, no retention scripts. If you cancel, you keep access until the end of the period you have already paid for.',
  },
  {
    q: 'Is my health data private?',
    a: 'Your record is yours. It is encrypted at rest, never sold, never shared, and you can export or delete it whenever you like. Kindra is a tool you keep — not a service that owns your data.',
  },
  {
    q: 'What if I was invited as a Founder Member?',
    a: 'Founder Members keep lifetime access — no billing, no renewals. You will see a quiet ✦ Founder Member badge in your profile and Kindra will simply not ask you to pay.',
  },
];

const FREE_TRIAL = [
  'Full access for 30 days',
  'Personal AI health chat with memory',
  'Unlimited document upload + extraction',
  'Health timeline + condition tracking',
  'Appointment preparation',
  'Daily insights + Today\u2019s focus',
  'Weekly recap email',
  'No credit card required',
];

const ACTIVE = [
  'Everything in the trial — no caps',
  'Continued access to your full health record',
  'Personalised lessons every week',
  'Priority support',
  'Founder updates as new features ship',
  'Cancel any time, one click',
];

export default function PricingPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#FBFAF6] text-[#0E1C2E]" data-testid="pricing-page">
      {/* Top nav */}
      <header className="max-w-5xl mx-auto px-6 lg:px-10 pt-8 pb-2 flex items-center justify-between">
        <Link to="/" className="font-serif text-[20px] tracking-tight text-[#0E1C2E]" data-testid="pricing-logo">
          Kindra
        </Link>
        <nav className="flex items-center gap-5 text-[13px]">
          <Link to="/" className="text-slate-500 hover:text-[#0E1C2E] transition-colors">How it works</Link>
          <Link to="/login" className="text-slate-500 hover:text-[#0E1C2E] transition-colors" data-testid="pricing-signin-link">Sign in</Link>
          <button
            onClick={() => navigate('/register')}
            className="bg-[#0E1C2E] text-white text-[13px] font-medium px-4 py-2 rounded-full hover:bg-[#1A2A3F] transition-colors"
            data-testid="pricing-start-trial-top"
          >
            Start free trial
          </button>
        </nav>
      </header>

      {/* Hero — Stage 9 polish: lead with the price, not the brand line.
          Audit P3 fix: pricing page no longer repeats landing's H1.
          Above-fold = price → ticks → CTA → anchor. Single decision. */}
      <section className="max-w-3xl mx-auto px-6 lg:px-10 pt-12 sm:pt-16 pb-10 text-center">
        <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#235347] mb-5">Pricing</p>
        <h1
          className="font-serif text-[44px] sm:text-[60px] lg:text-[72px] leading-[1.0] tracking-[-0.02em] text-[#0E1C2E] font-normal"
          data-testid="pricing-h1"
        >
          $9.99 a month.
        </h1>
        <p
          className="font-serif italic text-[20px] sm:text-[22px] text-[#3F4D5E] leading-snug mt-3"
          data-testid="pricing-subhead"
        >
          That's it. No tiers. No timers.
        </p>

        {/* 4 trust ticks — single row on desktop, 2x2 on mobile */}
        <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-y-2.5 gap-x-4 max-w-xl mx-auto text-[12.5px] text-slate-600" data-testid="pricing-ticks">
          <div className="inline-flex items-center justify-center gap-1.5">
            <CheckCircle size={13} weight="duotone" className="text-emerald-500 flex-shrink-0" />
            30 days free
          </div>
          <div className="inline-flex items-center justify-center gap-1.5">
            <CheckCircle size={13} weight="duotone" className="text-emerald-500 flex-shrink-0" />
            No credit card
          </div>
          <div className="inline-flex items-center justify-center gap-1.5">
            <CheckCircle size={13} weight="duotone" className="text-emerald-500 flex-shrink-0" />
            Cancel anytime
          </div>
          <div className="inline-flex items-center justify-center gap-1.5">
            <CheckCircle size={13} weight="duotone" className="text-emerald-500 flex-shrink-0" />
            Your record stays yours
          </div>
        </div>

        {/* Anchor copy — frames $9.99 against everyday realities */}
        <p className="mt-7 text-[13.5px] sm:text-[14.5px] text-slate-500 leading-relaxed max-w-md mx-auto italic" data-testid="pricing-anchor">
          Less than one missed prescription, one rebooked appointment, or one panic-Google at 2am.
        </p>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
          <button
            onClick={() => navigate('/register')}
            data-testid="pricing-start-trial-hero"
            className="inline-flex items-center gap-2 bg-[#0E1C2E] text-white text-[14px] font-medium px-7 py-3.5 rounded-lg hover:bg-[#1A2A3F] transition-colors group"
          >
            Start Free Trial
            <ArrowRight size={14} weight="bold" className="transition-transform group-hover:translate-x-0.5" />
          </button>
          <Link
            to="/"
            data-testid="pricing-how-it-works"
            className="text-[12.5px] text-slate-400 hover:text-[#0E1C2E] underline decoration-slate-200 underline-offset-4 hover:decoration-[#0E1C2E] transition-colors px-2 py-3"
          >
            See how it works
          </Link>
        </div>

        <p className="text-[12px] text-slate-400 mt-5">
          After 30 days, decide if you want to continue with Kindra Premium.
        </p>
      </section>

      {/* Free Trial vs Active Membership comparison */}
      <section className="max-w-5xl mx-auto px-6 lg:px-10 pb-16">
        <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-500 text-center mb-8">
          What you get
        </p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-6">
          <article className="bg-white border border-[#E5DFD0] rounded-2xl p-7" data-testid="plan-trial">
            <div className="flex items-center gap-2 mb-2">
              <Heart size={14} weight="duotone" className="text-[#235347]" />
              <h3 className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#235347]">30-day free trial</h3>
            </div>
            <p className="font-serif text-[24px] text-[#0E1C2E] leading-tight tracking-[-0.012em]">Try Kindra fully — on us.</p>
            <p className="text-[13px] text-slate-500 mt-2 mb-5 leading-relaxed">
              No credit card. No countdown timers. Just full access while you decide.
            </p>
            <ul className="space-y-2.5">
              {FREE_TRIAL.map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-[13.5px] text-[#0E1C2E] leading-snug">
                  <CheckCircle size={14} weight="duotone" className="text-emerald-500 mt-0.5 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </article>

          <article className="bg-[#0E1C2E] text-white rounded-2xl p-7 relative overflow-hidden" data-testid="plan-active">
            <div
              aria-hidden
              className="absolute inset-0 opacity-[0.07] pointer-events-none"
              style={{
                backgroundImage:
                  'radial-gradient(circle at 30% 0%, #2FB3A0 0%, transparent 45%), radial-gradient(circle at 80% 100%, #CDA873 0%, transparent 50%)',
              }}
            />
            <div className="relative">
              <div className="flex items-center gap-2 mb-2">
                <Sparkle size={14} weight="fill" className="text-[#CDA873]" />
                <h3 className="text-[10px] font-semibold uppercase tracking-[0.2em] text-white/70">Active membership</h3>
              </div>
              <p className="font-serif text-[24px] leading-tight tracking-[-0.012em]">$9.99 AUD/month, after 30 days.</p>
              <p className="text-[13px] text-white/65 mt-2 mb-5 leading-relaxed">
                Continue using Kindra only if it has earned a place in your week.
              </p>
              <ul className="space-y-2.5">
                {ACTIVE.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-[13.5px] text-white/85 leading-snug">
                    <CheckCircle size={14} weight="duotone" className="text-emerald-400/80 mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </article>
        </div>
      </section>

      {/* Trust note */}
      <section className="max-w-3xl mx-auto px-6 lg:px-10 pb-16">
        <div className="bg-[#F4EFE3]/60 border border-[#E5DFD0] rounded-2xl p-7 sm:p-8" data-testid="pricing-trust">
          <div className="flex items-start gap-3">
            <Shield size={18} weight="duotone" className="text-[#235347] mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-serif text-[20px] text-[#0E1C2E] leading-tight tracking-[-0.012em] mb-2">
                Your health history stays yours.
              </h3>
              <p className="text-[13.5px] text-[#3F4D5E] leading-relaxed">
                Encrypted at rest. Never sold. Never shared. Export or delete with one click. We are a tool you keep — not a service that owns your data.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-6 lg:px-10 pb-20">
        <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-500 text-center mb-8">
          Common questions
        </p>
        <div className="space-y-3" data-testid="pricing-faq">
          {FAQ.map((item, i) => (
            <details
              key={i}
              className="group bg-white border border-[#E5DFD0] rounded-xl px-5 py-4 [&_summary::-webkit-details-marker]:hidden"
              data-testid={`pricing-faq-item-${i}`}
            >
              <summary className="flex items-center justify-between cursor-pointer list-none">
                <span className="text-[14px] font-medium text-[#0E1C2E]">{item.q}</span>
                <span className="text-slate-400 text-[13px] group-open:rotate-180 transition-transform">▾</span>
              </summary>
              <p className="text-[13.5px] text-slate-600 leading-relaxed mt-3 pr-6">{item.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Stage 15 P3 — conversion confidence. Sits between the pricing card
          stack above and the bottom CTA — calm, factual reassurance. */}
      <section className="max-w-3xl mx-auto px-6 lg:px-10 pb-12">
        <WhyMembersStay heading="Why members stay" />
      </section>

      {/* Stage 20B+ Wave B — sample appointment brief link. Lets wavering
          buyers see the real product output before committing. */}
      <section className="max-w-3xl mx-auto px-6 lg:px-10 pb-6">
        <a
          href="/sample-brief"
          target="_blank"
          rel="noreferrer"
          className="block rounded-2xl border border-[#E5DFD0] bg-white p-5 md:p-6 hover:bg-[#FBFAF6] transition-colors group"
          data-testid="pricing-sample-brief-link"
        >
          <div className="flex items-start gap-4">
            <span
              aria-hidden
              className="inline-flex items-center justify-center w-10 h-10 rounded-xl flex-shrink-0"
              style={{ backgroundColor: '#FBFAF6', border: '1px solid rgba(14,28,46,0.08)' }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2FB3A0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="8" y1="13" x2="16" y2="13" />
                <line x1="8" y1="17" x2="13" y2="17" />
              </svg>
            </span>
            <div className="flex-1 min-w-0">
              <p className="font-sans text-[10.5px] md:text-[11px] uppercase font-medium" style={{ color: '#2FB3A0', letterSpacing: '0.18em' }}>
                See the output first
              </p>
              <p className="font-serif text-[18px] md:text-[20px] leading-[1.35] tracking-[-0.005em] mt-1" style={{ color: '#0E1C2E' }}>
                An example appointment brief
              </p>
              <p className="font-sans text-[13px] md:text-[13.5px] leading-[1.6] mt-2 text-slate-500">
                Open a real example of what Kindra writes before each visit. Print it, save it as PDF, or scroll — you'll know in thirty seconds whether this helps.
              </p>
            </div>
            <span className="text-slate-400 text-[14px] mt-[4px] group-hover:translate-x-0.5 transition-transform" aria-hidden>→</span>
          </div>
        </a>
      </section>

      {/* Stage 20B+ Wave A — Stripe payment reassurance strip, above the
          bottom CTA. Calm, specific, aligns the commit moment with trust. */}
      <section className="max-w-3xl mx-auto px-6 lg:px-10 pb-6">
        <div
          className="rounded-2xl border border-[#E5DFD0] bg-white p-5 md:p-6 flex items-start gap-3"
          data-testid="pricing-stripe-reassurance"
        >
          <span
            aria-hidden
            className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#FBFAF6] flex-shrink-0 mt-[2px]"
            style={{ border: '1px solid rgba(14,28,46,0.08)' }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2FB3A0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </span>
          <div>
            <p className="font-sans text-[13.5px] md:text-[14px] font-medium text-[#0E1C2E] leading-[1.45]">
              Payment handled securely by Stripe.
            </p>
            <p className="font-sans text-[12.5px] md:text-[13px] text-slate-500 leading-[1.6] mt-1.5">
              Your card details never touch our servers. No auto-renewal lock-in — cancel anytime from the Billing page inside the app.
            </p>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="max-w-3xl mx-auto px-6 lg:px-10 pb-24 text-center">
        <p className="font-serif italic text-[20px] sm:text-[22px] text-[#0E1C2E] leading-snug max-w-xl mx-auto">
          We want people to experience Kindra because it genuinely helps.
        </p>
        <button
          onClick={() => navigate('/register')}
          data-testid="pricing-start-trial-bottom"
          className="inline-flex items-center gap-2 bg-[#0E1C2E] text-white text-[14px] font-medium px-6 py-3.5 rounded-lg hover:bg-[#1A2A3F] transition-colors group mt-7"
        >
          Start Free Trial
          <ArrowRight size={14} weight="bold" className="transition-transform group-hover:translate-x-0.5" />
        </button>
        <p className="text-[12px] text-slate-400 mt-4">
          30 days free · No credit card · Then $9.99 AUD/month · Cancel anytime
        </p>
      </section>

      {/* Footer — Stage 20B+ Wave A — full trust nav */}
      <footer className="border-t border-[#E5DFD0] py-10 text-center text-[12px] text-slate-400">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
          <Link to="/" className="hover:text-[#0E1C2E] transition-colors">Kindra</Link>
          <Link to="/about" className="hover:text-[#0E1C2E] transition-colors">About</Link>
          <Link to="/faq" className="hover:text-[#0E1C2E] transition-colors">FAQ</Link>
          <Link to="/privacy" className="hover:text-[#0E1C2E] transition-colors">Privacy</Link>
          <Link to="/security" className="hover:text-[#0E1C2E] transition-colors">Security</Link>
          <Link to="/terms" className="hover:text-[#0E1C2E] transition-colors">Terms</Link>
          <Link to="/medical-disclaimer" className="hover:text-[#0E1C2E] transition-colors">Medical Disclaimer</Link>
          <Link to="/contact" className="hover:text-[#0E1C2E] transition-colors">Contact</Link>
        </div>
        <p className="mt-4 text-[11.5px] text-slate-400">© 2026 Kindra AI Pty Ltd · Based in Brisbane, Queensland, Australia</p>
      </footer>
    </div>
  );
}
